// src/app/admin/notification/provider/NotificationProvider.js
import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showMessage } from 'app/store/fuse/messageSlice';

import { getRandomString } from 'modules/base/utils/GenericUtils';
import NotificationTableService from 'app/admin/notification/service/NotificationTableService';

const URL =
	`ws${process.env.REACT_APP_WS_SECURE === 'true' ? 's' : ''}://` +
	`${process.env.REACT_APP_WEBSOCKET === '' ? window.location.host : process.env.REACT_APP_WEBSOCKET}` +
	`${process.env.REACT_APP_WS_PORT?.length ? `:${process.env.REACT_APP_WS_PORT}` : ''}` +
	`${process.env.REACT_APP_NOTIFICATION_WS_PATH || ''}`;

const RECONNECT_BASE_MS = 2000;
const RECONNECT_MAX_MS = 60000;
const RECONNECT_JITTER = 0.2;
const RECONNECT_MAX_ATTEMPTS = 10;

const PING_INTERVAL_MS = Number(process.env.REACT_APP_WS_PING_MS || 30000);

function notifyConnected() { }
function notifyDisconnected(ev) { }
function notifyError(err) { }
function notifyReconnecting(attempt) { }
function notifyReconnectIn(delayMs, attempt) { }
function notifyReconnectCancelled() { }

function getBackoffDelayMs(nthAttempt) {
	const exp = Math.min(RECONNECT_BASE_MS * Math.pow(2, Math.max(0, nthAttempt - 1)), RECONNECT_MAX_MS);
	const jitter = exp * RECONNECT_JITTER * (Math.random() * 2 - 1);
	return Math.max(0, Math.floor(exp + jitter));
}

const NotificationsContext = createContext(null);

export default function NotificationProvider({ children }) {
	const dispatch = useDispatch();

	// belépés jel (nálatok: role tömb üres -> Guest)
	const isLoggedIn = useSelector(({ auth }) => !!auth?.user?.role?.length);

	const wsRef = useRef(null);
	const reconnectTimerRef = useRef(null);
	const attemptRef = useRef(0);
	const manualCloseRef = useRef(false);
	const pingTimerRef = useRef(null);

	const sessionRef = useRef(null);
	const ensureSessionPromiseRef = useRef(null);

	const [sessionName, setSessionName] = useState(null);

	const [update, setUpdate] = useState(0);
	const [status, setStatus] = useState('idle');
	const [lastEvent, setLastEvent] = useState(null);
	const [error, setError] = useState(null);

	const log = useCallback((...args) => {
		try { console.log('[Notifications]', ...args); } catch (e) { console.log(e); }
	}, []);

	const clearReconnectTimer = useCallback(() => {
		if (reconnectTimerRef.current) {
			clearTimeout(reconnectTimerRef.current);
			reconnectTimerRef.current = null;
		}
	}, []);

	const stopPing = useCallback(() => {
		if (pingTimerRef.current) {
			clearInterval(pingTimerRef.current);
			pingTimerRef.current = null;
		}
	}, []);

	const startPing = useCallback((ws) => {
		if (!PING_INTERVAL_MS || PING_INTERVAL_MS <= 0 || !ws) return;

		stopPing();

		try {
			if (ws.readyState === WebSocket.OPEN) {
				ws.send(JSON.stringify({ ping: Date.now() }));
				log('ping (immediate)', { ts: Date.now() });
			}
		} catch (e) { }

		pingTimerRef.current = setInterval(() => {
			try {
				const sock = wsRef.current;
				if (sock && sock.readyState === WebSocket.OPEN) {
					sock.send(JSON.stringify({ ping: Date.now() }));
				}
			} catch (e) { }
		}, PING_INTERVAL_MS);
	}, [stopPing, log]);

	const handleIncomingMessage = useCallback((data) => {
		let msg = null;

		try {
			msg = JSON.parse(data);
		} catch (e) {
			log('message (raw)', data);
			return;
		}

		log('message', msg);

		if (msg && msg.name === 'creo.nboss.notification.toast') {
			const toastMessage = msg.parameters && msg.parameters.message;
			if (toastMessage) dispatch(showMessage({ message: toastMessage }));
		}

		if (msg && msg.name === 'creo.nboss.notification.card') {
			setUpdate(prev => prev + 1);
		}
	}, [log, dispatch]);

	const scheduleReconnect = useCallback(() => {
		if (!isLoggedIn) return;
		if (manualCloseRef.current) return;

		if (RECONNECT_MAX_ATTEMPTS !== null && RECONNECT_MAX_ATTEMPTS !== Infinity && attemptRef.current >= RECONNECT_MAX_ATTEMPTS) {
			setStatus('error');
			const err = new Error('Max reconnect attempts reached');
			setError(err);
			notifyError(err);
			return;
		}

		attemptRef.current += 1;
		const attempt = attemptRef.current;
		const delay = getBackoffDelayMs(attempt);

		clearReconnectTimer();
		notifyReconnectIn(delay, attempt);

		reconnectTimerRef.current = setTimeout(() => {
			if (!isLoggedIn) return;
			if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) return;
			notifyReconnecting(attempt);
			setStatus('connecting');
			connect();
		}, delay);
	}, [clearReconnectTimer, isLoggedIn]);

	const attachWsHandlers = useCallback((ws) => {
		ws.addEventListener('open', () => {
			if (!isLoggedIn) {
				try { ws.close(1000, 'not logged in'); } catch (e) { }
				return;
			}

			setStatus('open');
			setError(null);
			attemptRef.current = 0;
			clearReconnectTimer();
			manualCloseRef.current = false;
			notifyConnected();

			startPing(ws);
		});

		ws.addEventListener('message', (ev) => {
			setLastEvent(ev);
			handleIncomingMessage(ev.data);
		});

		ws.addEventListener('error', (ev) => {
			setStatus('error');
			setError(ev);
			notifyError(ev);
			log('error', ev);

			stopPing();
		});

		ws.addEventListener('close', (ev) => {
			wsRef.current = null;
			stopPing();

			const isNormalClose = ev && ev.code === 1000;
			if (!isLoggedIn || manualCloseRef.current || isNormalClose) {
				setStatus('closed');
				notifyDisconnected(ev);
				return;
			}

			setStatus('closed');
			notifyDisconnected(ev);
			scheduleReconnect();
		});
	}, [clearReconnectTimer, log, scheduleReconnect, startPing, stopPing, handleIncomingMessage, isLoggedIn]);

	const connect = useCallback(() => {
		if (!isLoggedIn) return null;

		if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
			return wsRef.current;
		}

		try {
			setStatus('connecting');
			setError(null);

			const ws = new WebSocket(URL);
			wsRef.current = ws;
			attachWsHandlers(ws);
			return ws;
		} catch (err) {
			setStatus('error');
			setError(err);
			notifyError(err);
			scheduleReconnect();
			return null;
		}
	}, [attachWsHandlers, scheduleReconnect, isLoggedIn]);

	const disconnect = useCallback(() => {
		manualCloseRef.current = true;
		clearReconnectTimer();
		stopPing();

		if (wsRef.current) {
			try { wsRef.current.close(1000, 'client disconnect'); } catch (e) { console.log(e); }
			wsRef.current = null;
		}

		setStatus('closed');
		notifyDisconnected(null);
		log('disconnected (manual)');
	}, [clearReconnectTimer, log, stopPing]);

	const reconnect = useCallback(() => {
		if (!isLoggedIn) return null;

		manualCloseRef.current = false;
		clearReconnectTimer();
		stopPing();
		notifyReconnectCancelled();

		if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED && wsRef.current.readyState !== WebSocket.CLOSING) {
			try { wsRef.current.close(1000, 'manual reconnect'); } catch (e) { console.log(e); }
			wsRef.current = null;
		}

		setStatus('connecting');
		return connect();
	}, [clearReconnectTimer, connect, stopPing, isLoggedIn]);

	// belépés-gate: csak logged in esetén connect, logout esetén teardown
	useEffect(() => {
		if (!isLoggedIn) {
			manualCloseRef.current = true;
			clearReconnectTimer();
			stopPing();

			try { wsRef.current && wsRef.current.close(1000, 'not logged in'); } catch (e) { }
			wsRef.current = null;

			attemptRef.current = 0;
			setStatus('closed');
			setError(null);
			setLastEvent(null);

			// session reset logoutnál
			sessionRef.current = null;
			ensureSessionPromiseRef.current = null;
			setSessionName(null);

			return;
		}

		manualCloseRef.current = false;
		connect();

		const handleOnline = () => {
			if (!isLoggedIn) return;
			if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) reconnect();
		};

		window.addEventListener('online', handleOnline);

		return () => {
			window.removeEventListener('online', handleOnline);
			manualCloseRef.current = true;
			clearReconnectTimer();
			stopPing();
			try { wsRef.current && wsRef.current.close(1000, 'unmount'); } catch (e) { }
			wsRef.current = null;
			setStatus('closed');
		};
	}, [isLoggedIn, connect, reconnect, clearReconnectTimer, stopPing]);

	// session biztosítás: egyszer fusson, párhuzamos getList hívásoknál is
	const ensureSession = useCallback(async () => {
		if (!isLoggedIn) throw new Error('Not logged in');

		if (sessionRef.current) return;

		if (ensureSessionPromiseRef.current) {
			await ensureSessionPromiseRef.current;
			return;
		}

		ensureSessionPromiseRef.current = (async () => {
			const s = getRandomString();
			await NotificationTableService.getInstance(s, true, null);
			sessionRef.current = s;
			setSessionName(s);
		})().finally(() => {
			ensureSessionPromiseRef.current = null;
		});

		await ensureSessionPromiseRef.current;
	}, [isLoggedIn]);

	const getList = useCallback(async () => {
		if (!isLoggedIn) return Promise.resolve({ list: [], total: 0 });

		await ensureSession();
		const s = sessionRef.current;

		return NotificationTableService.getList(s, 0, 0)
			.then((resp) => resp)
			.catch((err) => { console.error(err); throw err; });
	}, [ensureSession, isLoggedIn]);

	const deleteById = useCallback((id) => {
		if (!isLoggedIn) return;
		if (!sessionRef.current) return;

		NotificationTableService.userDeleteById(sessionRef.current, id).then(() => {
			setUpdate(prev => prev + 1);
		});
	}, [isLoggedIn]);

	const deleteAll = useCallback(() => {
		if (!isLoggedIn) return;
		NotificationTableService.userDeleteAll().then(() => {
			setUpdate(prev => prev + 1);
		});
	}, [isLoggedIn]);

	const markAsRead = useCallback((id, state) => {
		if (!isLoggedIn) return;

		if (state) {
			NotificationTableService.markAsRead(id).then(() => { setUpdate(prev => prev + 1); });
		} else {
			NotificationTableService.markAsUnRead(id).then(() => { setUpdate(prev => prev + 1); });
		}
	}, [isLoggedIn]);

	const value = useMemo(() => ({
		status,
		lastEvent,
		error,
		isConnected: status === 'open',
		connect,
		disconnect,
		reconnect,
		getList,
		deleteById,
		deleteAll,
		markAsRead,
		update,
		sessionName
	}), [
		status,
		lastEvent,
		error,
		connect,
		disconnect,
		reconnect,
		getList,
		deleteById,
		deleteAll,
		markAsRead,
		update,
		sessionName
	]);

	return (
		<NotificationsContext.Provider value={value}>
			{children}
		</NotificationsContext.Provider>
	);
}

export function useNotifications() {
	const ctx = useContext(NotificationsContext);
	if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
	return ctx;
}
